import { useCallback, useEffect, useState } from "react";
import { IFRAME_ID } from "@circle-react-shared/BuilderUI/Frames/constants";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useIFrameDocument } from "@circle-react-shared/BuilderUI/Hooks/useIFrameDocument";

export interface Positions {
  elementRect: DOMRect;
  iframeRect: DOMRect;
}

export interface UseCanvasToolsPositionsReturn {
  positions: Positions | null;
  selectedBlock: { id: string } | null;
  updatePosition: () => void;
}

export const useCanvasToolsPositions = (): UseCanvasToolsPositionsReturn => {
  // Combined subscription for selected block data
  const { selectedBlockPath, selectedBlock } = useBuilderStore(state => {
    const value = state.getSelectedBlock();
    return {
      selectedBlockPath: state.selectedBlockPath,
      selectedBlock: value?.block,
    };
  });
  const iFrameDocument = useIFrameDocument(IFRAME_ID, selectedBlockPath ?? "");

  const [positions, setElementRect] = useState<Positions | null>(null);

  const getElementRect = useCallback(() => {
    if (!selectedBlock?.id || !iFrameDocument) {
      return null;
    }

    const element = iFrameDocument.getElementById(selectedBlock.id);
    const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;

    if (!element || !iframe) {
      return null;
    }

    const elementRect = element.getBoundingClientRect();
    const iframeRect = iframe.getBoundingClientRect();

    return {
      elementRect,
      iframeRect,
    };
  }, [selectedBlock, iFrameDocument]);

  const updatePosition = useCallback(() => {
    const rect = getElementRect();
    setElementRect(rect);
  }, [getElementRect]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition, selectedBlockPath]);

  return {
    positions,
    selectedBlock: selectedBlock || null,
    updatePosition,
  };
};
